import * as React from 'react';

import {type Type, Types, Plugins} from '@packages/systems/wfdl';
import {getName} from '@packages/systems/wfdl-Qualified';
import {
  styled,
  styledDiv,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';

import {FinePrint, Subheading} from '@packages/systems/design-system/Text';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';

const TooltipContentWrapper = emotionStyled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: tokens.space[1],
  color: tokens.colors.border3,
});

const StyledSubheading = styled(Subheading)(({theme: {gutters}}) => ({
  marginBottom: gutters.x4,
  color: 'inherit',
}));

const StyledFinePrint = styled(FinePrint)({
  display: 'inline-flex',
  alignItems: 'center',
  color: 'inherit',
});

const IconWrapper = emotionStyled('div')({
  color: 'inherit',
  marginRight: tokens.space[1],
});

const DetailsRow = emotionStyled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  marginTop: tokens.space[1],
});

const DetailsLabel = styled(StyledFinePrint)(({theme: {gutters}}) => ({
  marginRight: gutters.x8,
}));

const Highlight = styledDiv(({theme: {control, gutters}}) => ({
  backgroundColor: 'rgba(0, 0, 0, .05)',
  borderRadius: control.borderRadius,
  paddingRight: gutters.base,
  paddingLeft: gutters.base,
  marginLeft: gutters.base,
}));

const renderArg = (arg: Type) => {
  const {body} = Types.deconstructGeneric(arg);
  const {items, tail} = Types.deconstructRow(body);
  const labels = Object.keys(items);

  if (labels.length || Types.isRowEmpty(tail)) {
    // Ignore rows in the tooltip for now; they show up in the output tree
    return null;
  }

  const tyApp = Types.deconstructTypeApplication(body);
  return (
    <Highlight>{tyApp ? getName(tyApp.con) : Types.print(body)}</Highlight>
  );
};

// @ts-expect-error - TS7031 - Binding element 'type' implicitly has an 'any' type.
const TypeDisplay = ({type}) => {
  const {body} = Types.deconstructGeneric(type);
  const tyApp = Types.deconstructTypeApplication(body);
  if (!tyApp) return Types.print(body);
  const args = tyApp.args.map(renderArg);
  return (
    <>
      {getName(tyApp.con)} {args}
    </>
  );
};

// @ts-expect-error - TS7031 - Binding element 'icon' implicitly has an 'any' type. | TS7031 - Binding element 'header' implicitly has an 'any' type. | TS7031 - Binding element 'path' implicitly has an 'any' type. | TS7031 - Binding element 'type' implicitly has an 'any' type.
function TypeViewTooltipContent({icon, header, path, type}) {
  return (
    <TooltipContentWrapper>
      <StyledSubheading>{header}</StyledSubheading>
      <DetailsRow>
        <DetailsLabel>Type: </DetailsLabel>
        <StyledFinePrint>
          <IconWrapper>
            <Plugins.PluginIcon icon={icon} />
          </IconWrapper>
          <TypeDisplay type={type} />
        </StyledFinePrint>
      </DetailsRow>
      {path && (
        <DetailsRow>
          <DetailsLabel>Path:</DetailsLabel>
          <StyledFinePrint>{path}</StyledFinePrint>
        </DetailsRow>
      )}
    </TooltipContentWrapper>
  );
}

export function TypeTooltip(props: {
  children: React.ReactNode;
  icon: Plugins.QualifiedPluginIcon;
  label: React.ReactNode;
  path?: string;
  type: Type;
}) {
  const splitPath = props.path?.split('.');
  const formattedPath = splitPath?.length ? splitPath.join(' / ') : null;
  return (
    <TooltipTrigger
      alternate
      content={
        <TypeViewTooltipContent
          icon={props.icon}
          header={props.label}
          path={formattedPath}
          type={props.type}
        />
      }
      placement="bottom"
      panel
    >
      {props.children}
    </TooltipTrigger>
  );
}
